// Make permissioned attachment URLs work for direct browser access.
//
// Uploads embed a link to `/api/v2/attachments/:id/download` — file chips as
// <a href>, pasted images as <img src>. That endpoint requires auth, but the
// browser can't send our `Authorization: Bearer …` header on a plain navigation
// (clicking the link / opening a new tab) or on an <img> load. So those requests
// arrive unauthenticated → "Authentication required".
//
// The fix: ask the API (with our auth header, via the JSON client) for a
// short-lived *signed* URL and use that instead. We do it lazily —
//   • anchors: intercept the click, mint a signed URL, open it;
//   • images: rewrite the src to a signed URL as they appear in the DOM
//             (including comments rendered via dangerouslySetInnerHTML).

import { api } from './api';

// The permissioned download URL, capturing the attachment id. Matches both
// relative (/api/v2/…) and absolute (https://host/api/v2/…) forms. cuid ids are
// alphanumeric, so the character class is safe.
const DOWNLOAD_RE = /\/api\/v2\/attachments\/([A-Za-z0-9]+)\/download(?:[?#]|$)/;

// Cache signed URLs per id so re-rendered images / repeat clicks don't each hit
// the network. Refresh a little before the server's 10-minute token expiry.
const cache = new Map<string, { url: string; at: number }>();
const REFRESH_MS = 8 * 60 * 1000;

async function signedUrlFor(id: string): Promise<string> {
  const hit = cache.get(id);
  if (hit && Date.now() - hit.at < REFRESH_MS) return hit.url;
  const { url } = await api.get<{ url: string }>(`/v2/attachments/${id}/signed-url`);
  cache.set(id, { url, at: Date.now() });
  return url;
}

/** The attachment id in an unsigned download URL, or null (incl. already-signed). */
function unsignedId(raw: string | null): string | null {
  if (!raw || raw.includes('token=')) return null;
  return raw.match(DOWNLOAD_RE)?.[1] ?? null;
}

function onClick(e: MouseEvent) {
  if (e.defaultPrevented) return;
  const anchor = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
  const id = anchor && unsignedId(anchor.getAttribute('href'));
  if (!id) return;
  e.preventDefault();
  // Open the tab synchronously (inside the user gesture) so popup blockers allow
  // it, then point it at the signed URL once minted.
  const win = window.open('', '_blank');
  signedUrlFor(id)
    .then((url) => {
      if (win) win.location.href = url;
      else window.location.href = url; // popup blocked → same-tab fallback
    })
    .catch(() => win?.close());
}

function rewriteImages(root: ParentNode | Element) {
  const imgs: HTMLImageElement[] = [];
  if (root instanceof HTMLImageElement) imgs.push(root);
  root.querySelectorAll?.('img[src]').forEach((el) => imgs.push(el as HTMLImageElement));
  for (const img of imgs) {
    const id = unsignedId(img.getAttribute('src'));
    if (id) signedUrlFor(id).then((url) => { img.src = url; }).catch(() => undefined);
  }
}

let started = false;

/** Install the click interceptor + image rewriter. Idempotent. */
export function installAttachmentLinks(): void {
  if (started || typeof document === 'undefined') return;
  started = true;

  document.addEventListener('click', onClick, true);
  rewriteImages(document);

  // Catch images added later (comments, editor inserts) and any src swap. Setting
  // a signed src (which contains `token=`) is ignored by unsignedId, so no loop.
  new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'attributes') {
        const t = m.target as Element;
        if (t instanceof HTMLImageElement) rewriteImages(t);
        continue;
      }
      m.addedNodes.forEach((n) => {
        if (n.nodeType === 1) rewriteImages(n as Element);
      });
    }
  }).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src'],
  });
}
